<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  nameList,
  nameCreate,
  namePatch,
  nameDel,
} from "@/common/api/rest/game/name";

import type { NameType, NamePayloadType } from "@/common/types/name";

import FormName from "@/components/forms/form-name.vue";
import { baseConfirm, svgIcon } from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  ownerId: string;
}>();

const selected = ref<NameType | null>(null);
const list = ref<NameType[]>([]);

const showModalAdd = ref(false);
const showModalDel = ref(false);

function fetchData() {
  if (!props.ownerId) return;

  nameList(props.ownerId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        list.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
}

function addItemConfirm() {
  nameCreate(props.ownerId)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        addItemCancel();
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delItem(data: NameType) {
  showModalDel.value = true;
  selected.value = data;
}

function delItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delItemConfirm() {
  nameDel(selected.value?.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        delItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchItem(item_id: string, payload: NamePayloadType) {
  namePatch(item_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="list-data">
    <section class="header">
      <div class="title">
        {{ t("common.labels.other_names") }}
      </div>
    </section>

    <section class="list">
      <template v-for="item in list" :key="item.item_id">
        <div class="list-actions">
          <button
            type="button"
            class="btn btn-sm btn-danger px-3 py-2"
            @click="delItem(item)"
          >
            <svg-icon icon="trash" size="1.25rem" />
            {{ t("common.labels.delete") }}
          </button>
        </div>
        <form-name
          :data="item"
          :owner-id="props.ownerId"
          :is-patch="true"
          @update:data="patchItem(item.item_id, $event)"
        />
        <div class="separator" />
      </template>

      <template v-if="(list || []).length === 0">
        <div class="empty">
          {{ t("common.texts.empty_name") }}
        </div>
      </template>

      <div class="actions">
        <button
          type="button"
          class="btn btn-md btn-default uppercase px-4"
          @click="addItem()"
        >
          {{ t("common.texts.add_name") }}
        </button>
      </div>
    </section>

    <base-confirm
      :show="showModalAdd"
      :title="t('common.modals.titles.add_name').toUpperCase()"
      :message="t('common.modals.messages.add_name')"
      @cancel="addItemCancel()"
      @confirm="addItemConfirm()"
    />

    <base-confirm
      :show="showModalDel"
      :title="t('common.modals.titles.remove_name').toUpperCase()"
      :message="t('common.modals.messages.remove_name')"
      @cancel="delItemCancel()"
      @confirm="delItemConfirm()"
    />
  </article>
</template>

<style lang="scss" scoped>
.list-data {
  display: flex;
  flex-direction: column;
  width: inherit;
  gap: 1rem;

  .header {
    display: flex;
    flex-direction: column;
    width: inherit;
    gap: 0.5rem;

    .title {
      font-size: 1.2rem;
      text-transform: uppercase;
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;

    &-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;
    }

    .empty {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1rem;
    }

    .actions {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 1rem;
    }
  }

  .separator {
    height: 1px;
    background: var(--white-off);
    margin-block: 1.54rem;
  }
}
</style>
